import { ComplexValue, ValidationLevel, ValidationMessage } from 'ui';
import { languageValidationParameter, longDescriptionValidationParameter } from '../constants';
import { SubscriberGroupItem, SubscriberGroupItemValidation } from '../types-store';
import { ExternalMemberFormIds } from 'app/area/subscriber-groups/form/constants';
import { SubscriberGroupsTranslation } from 'app/area/subscriber-groups/constants';
import { isValidEmail } from 'app/infrastructure/validation/email';

const validationMessageHelper = (propName: string, messages: ValidationMessage | ValidationMessage[]) => {
  return {
    [propName]: {
      level: ValidationLevel.error,
      messages
    }
  };
};

export const validateSubscriberGroup = (
  subscriberGroupItem: SubscriberGroupItem,
  languageOptions: ComplexValue<number>[],
  translation: SubscriberGroupsTranslation
): SubscriberGroupItemValidation => {

  const defaultValidationError = {
    level: ValidationLevel.error,
    message: translation.must_be_filled_out
  };

  const validEmailValidationError = {
    level: ValidationLevel.error,
    message: translation.email_error
  };

  const duplicatedEmailValidationError = {
    level: ValidationLevel.error,
    message: translation.duplicated_email
  };

  const allLanguages = (languageOptions || [])
    .map(option => (subscriberGroupItem?.language || [])
      .find(language => language.value === option.value)
      || { value: option.value }
    );

  const allLongDescriptionLanguages = (languageOptions || [])
    .map(option => (subscriberGroupItem?.longDescription || [])
      .find(language => language.value === option.value)
      || { value: option.value }
    );

  const languageDescriptionValidation = allLanguages
    .map(language =>
      language.description === undefined || language.description.trim() === ''
        ? {
          ...defaultValidationError,
          parameters: {
            [languageValidationParameter]: language.value
          }
        } as ValidationMessage
        : undefined
    )
    .filter(validation => !!validation);

  const languageLongDescriptionValidation = subscriberGroupItem.selfService
    ? allLongDescriptionLanguages
      .map(language =>
        language.description === undefined || language.description.trim() === ''
          ? {
            ...defaultValidationError,
            parameters: {
              [longDescriptionValidationParameter]: language.value
            }
          } as ValidationMessage
          : undefined
      )
      .filter(validation => !!validation)
    : [];

  const duplicatedEmails = subscriberGroupItem?.externalMembers?.reduce((acc, current) => {
    const items = subscriberGroupItem?.externalMembers?.filter(item => item.emailAddress === current.emailAddress);
    if (items.length > 1) {
      acc = [...new Set([...acc, ...items.map(item => item.memberNo)])];
    }
    return acc;
  }, []);

  const emailValidation = subscriberGroupItem?.externalMembers
    ?.map(item =>
      item.emailAddress === undefined || item.emailAddress.trim() === ''
        ? {
          ...defaultValidationError,
          parameters: {
            id: item.memberNo
          }
        } as ValidationMessage
        : (

          !isValidEmail(item.emailAddress)
            ? { ...validEmailValidationError, parameters: { id: item.memberNo } } as ValidationMessage
            : (duplicatedEmails.includes(item.memberNo)
              ? { ...duplicatedEmailValidationError, parameters: { id: item.memberNo } } as ValidationMessage
              : undefined))

    )
    ?.filter(validation => !!validation);

  const nameValidation = subscriberGroupItem?.externalMembers
    ?.map(item =>
      item.name === undefined || item.name.trim() === ''
        ? {
          ...defaultValidationError,
          parameters: {
            id: item.memberNo
          }
        } as ValidationMessage
        : undefined
    )
    ?.filter(validation => !!validation);

  return {
    ...(languageDescriptionValidation.length > 0
      && validationMessageHelper(ExternalMemberFormIds.language, languageDescriptionValidation)),
    ...(languageLongDescriptionValidation.length > 0
      && validationMessageHelper(ExternalMemberFormIds.longDescription, languageLongDescriptionValidation)),
    ...(emailValidation?.length > 0 && validationMessageHelper(ExternalMemberFormIds.emailAddress, emailValidation)),
    ...(nameValidation?.length > 0 && validationMessageHelper(ExternalMemberFormIds.name, nameValidation))
  };
};
